import { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { getStagedPhotos } from '@/lib/db/photoStaging';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CheckCircle, AlertCircle, Plus, X, Clock, Camera, ScanLine, PenLine } from 'lucide-react';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { SignaturePad } from '@/components/SignaturePad';
import { useAuth } from '@/contexts/AuthContext';
import { useOrgSettings } from '@/hooks/useOrgSettings';
import { fetchFailureCodes, fetchActionCodes, fetchRootCauseCodes, rootCauseCategoryLabels } from '@/lib/api/settings';
import type { RootCauseCategory } from '@/lib/api/settings';
import { fetchTimeEntries } from '@/lib/api/timeEntries';
import { useSpareParts } from '@/stores/sparePartsStore';
import { useWorkOrders } from '@/stores/workOrderStore';
import { cn } from '@/lib/utils';
import type { ClosureData, WorkOrder, FixType, TestType } from '@/types/maintenance';

interface FinishWorkOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workOrder: WorkOrder;
  onConfirm: (data: ClosureData) => void;
}

function draftKey(woId: string) { return `draft_finish_wo_${woId}`; }

function defaultDuration(wo: WorkOrder): number {
  if (!wo.start_time) return 0;
  const mins = Math.round((Date.now() - new Date(wo.start_time).getTime()) / 60000);
  return Math.max(1, Math.round(mins / 5) * 5); // round to nearest 5
}

export function FinishWorkOrderDialog({ open, onOpenChange, workOrder, onConfirm }: FinishWorkOrderDialogProps) {
  const { t } = useTranslation(['maintenance', 'common']);
  const { user } = useAuth();
  const { settings } = useOrgSettings();
  const orgId = user?.organization_id ?? null;
  const isPreventive = workOrder.type === 'preventive';
  const showPartsPicker = !isPreventive; // corrective / improvement

  const { parts: allParts, getPartsByMold } = useSpareParts();
  const { getWorkOrdersByMold } = useWorkOrders();
  const linkedParts = workOrder.mold_id ? getPartsByMold(workOrder.mold_id) : [];
  const commonFailureParts = linkedParts.filter(mp => mp.is_common_failure_part);

  const [partsUsed, setPartsUsed] = useState<Array<{ part_id: string; part_name: string; unit: string; qty: number }>>([]);
  const [selectedPartId, setSelectedPartId] = useState('');
  const [partQty, setPartQty] = useState('1');
  const [partScannerOpen, setPartScannerOpen] = useState(false);

  const { data: failureCodes = [] } = useQuery({
    queryKey: ['failure_codes', orgId],
    queryFn: () => fetchFailureCodes(orgId),
    enabled: !!user && !isPreventive,
    staleTime: 5 * 60 * 1000,
  });

  const { data: actionCodes = [] } = useQuery({
    queryKey: ['action_codes', orgId],
    queryFn: () => fetchActionCodes(orgId),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const { data: rootCauseCodes = [] } = useQuery({
    queryKey: ['root_cause_codes', orgId],
    queryFn: () => fetchRootCauseCodes(orgId),
    enabled: !!user && !isPreventive,
    staleTime: 5 * 60 * 1000,
  });

  const { data: timeEntries = [] } = useQuery({
    queryKey: ['time_entries', workOrder.id],
    queryFn: () => fetchTimeEntries(workOrder.id),
    enabled: open,
    staleTime: 30_000,
  });
  const totalLoggedMinutes = timeEntries.reduce((sum, e) => sum + e.minutes, 0);

  // Photo requirement check
  const stagedPhotos = useLiveQuery(
    () => open ? getStagedPhotos(workOrder.id) : Promise.resolve([]),
    [open, workOrder.id],
    [],
  );
  const requiredImpacts: string[] = settings.require_photo_for_impacts ?? [];
  const photoRequired = workOrder.impact != null && requiredImpacts.includes(workOrder.impact);
  const hasPhoto = (workOrder.attachments?.length ?? 0) > 0 || (stagedPhotos?.length ?? 0) > 0;

  const activeFCs = failureCodes.filter(c => c.active);
  const activeACs = actionCodes.filter(c => c.active);
  const activeRCs = rootCauseCodes.filter(c => c.active);

  // Corrective / Improvement state
  const [failureCode, setFailureCode] = useState('');
  const [failureNotes, setFailureNotes] = useState('');
  const [actionCode, setActionCode] = useState('');
  const [whatWasDone, setWhatWasDone] = useState('');
  const [comment, setComment] = useState('');
  const [rootCauseCode, setRootCauseCode] = useState('');

  // Preventive state
  const [pmProblemsEncountered, setPmProblemsEncountered] = useState(false);
  const [pmProblemDescription, setPmProblemDescription] = useState('');
  const [pmWhatWasDone, setPmWhatWasDone] = useState('');
  const [pmCheckedCodes, setPmCheckedCodes] = useState<Set<string>>(new Set());
  const [pmChecklistResults, setPmChecklistResults] = useState<Record<string, { passed: boolean | null; notes: string }>>({});
  // Guided checklist step (index into activeACs)
  const [guidedStep, setGuidedStep] = useState(0); // code -> result

  // Corrective quality fields
  const [fixType, setFixType] = useState<FixType>('permanent');
  const [rootCause, setRootCause] = useState('');

  // Shared
  const [testedValidated, setTestedValidated] = useState(false);
  const [testType, setTestType] = useState<TestType | ''>('');
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [signatureDataUrl, setSignatureDataUrl] = useState<string | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);

  // Fix validation: detect repeat failures when a failure code is selected
  const repeatFailureInfo = useMemo(() => {
    if (isPreventive || !failureCode || !workOrder.mold_id) return null;
    const moldWOs = getWorkOrdersByMold(workOrder.mold_id);
    const windowMs = (settings.chronic_window_days ?? 90) * 86_400_000;
    const cutoff = Date.now() - windowMs;
    const priorMatches = moldWOs.filter(wo =>
      wo.id !== workOrder.id &&
      wo.status === 'closed' &&
      wo.type === 'corrective' &&
      wo.failure_code === failureCode &&
      wo.end_time &&
      new Date(wo.end_time).getTime() > cutoff,
    );
    if (priorMatches.length === 0) return null;
    return {
      count: priorMatches.length,
      lastWO: priorMatches.sort((a, b) => new Date(b.end_time!).getTime() - new Date(a.end_time!).getTime())[0],
    };
  }, [failureCode, workOrder.mold_id, workOrder.id, isPreventive, getWorkOrdersByMold, settings.chronic_window_days]);

  // Load draft or reset when dialog opens
  useEffect(() => {
    if (!open) return;

    const saved = localStorage.getItem(draftKey(workOrder.id));
    if (saved) {
      try {
        const d = JSON.parse(saved);
        setFailureCode(d.failureCode ?? '');
        setFailureNotes(d.failureNotes ?? '');
        setActionCode(d.actionCode ?? '');
        setWhatWasDone(d.whatWasDone ?? '');
        setComment(d.comment ?? '');
        setRootCause(d.rootCause ?? '');
        setRootCauseCode(d.rootCauseCode ?? '');
        setFixType(d.fixType ?? 'permanent');
        setTestedValidated(d.testedValidated ?? false);
        setTestType(d.testType ?? '');
        setPmProblemsEncountered(d.pmProblemsEncountered ?? false);
        setPmProblemDescription(d.pmProblemDescription ?? '');
        setPmWhatWasDone(d.pmWhatWasDone ?? '');
        setPmCheckedCodes(new Set(d.pmCheckedCodes ?? []));
        setPmChecklistResults(d.pmChecklistResults ?? {});
        setDurationMinutes(d.durationMinutes ?? defaultDuration(workOrder));
        setDraftRestored(true);
        setPartsUsed([]);
        setSelectedPartId('');
        setPartQty('1');
        return;
      } catch {
        // corrupted draft — fall through to fresh init
      }
    }

    // Fresh open — pre-fill from WO data
    const prefill = workOrder.failure_text
      || workOrder.closure_data?.failure
      || '';
    setFailureCode('');
    setFailureNotes(prefill);
    setActionCode('');
    setWhatWasDone('');
    setComment('');
    setRootCause('');
    setRootCauseCode('');
    setFixType('permanent');
    setTestedValidated(false);
    setTestType('');
    setPmProblemsEncountered(false);
    setPmProblemDescription('');
    setPmWhatWasDone('');
    setPmCheckedCodes(new Set());
    setPmChecklistResults({});
    setGuidedStep(0);
    setDurationMinutes(0);
    setSignatureDataUrl(null);
    setDraftRestored(false);
    setPartsUsed([]);
    setSelectedPartId('');
    setPartQty('1');
  }, [open, workOrder]);

  // Auto-save draft on every state change
  useEffect(() => {
    if (!open) return;
    const draft = {
      failureCode, failureNotes, actionCode, whatWasDone, comment,
      rootCause, rootCauseCode, fixType, testedValidated, testType,
      pmProblemsEncountered, pmProblemDescription, pmWhatWasDone,
      pmCheckedCodes: Array.from(pmCheckedCodes),
      pmChecklistResults,
      durationMinutes,
    };
    localStorage.setItem(draftKey(workOrder.id), JSON.stringify(draft));
  }, [
    open, failureCode, failureNotes, actionCode, whatWasDone, comment,
    rootCause, rootCauseCode, fixType, testedValidated, testType,
    pmProblemsEncountered, pmProblemDescription, pmWhatWasDone, pmCheckedCodes, durationMinutes,
    workOrder.id,
  ]);

  // Toggle PM checklist item — auto-append to pmWhatWasDone textarea
  const togglePmCode = (code: string, label: string) => {
    const next = new Set(pmCheckedCodes);
    if (next.has(code)) {
      next.delete(code);
      setPmWhatWasDone(prev => prev.replace(`✓ ${label}\n`, ''));
    } else {
      next.add(code);
      setPmWhatWasDone(prev => prev + `✓ ${label}\n`);
    }
    setPmCheckedCodes(next);
  };

  const addPartUsed = () => {
    if (!selectedPartId) return;
    const qty = parseInt(partQty, 10);
    if (!qty || qty <= 0) return;
    const part = allParts.find(p => p.id === selectedPartId);
    if (!part) return;
    setPartsUsed(prev => {
      const existing = prev.find(p => p.part_id === selectedPartId);
      if (existing) return prev.map(p => p.part_id === selectedPartId ? { ...p, qty: p.qty + qty } : p);
      return [...prev, { part_id: part.id, part_name: part.part_name, unit: part.unit, qty }];
    });
    setSelectedPartId('');
    setPartQty('1');
  };

  const removePartUsed = (partId: string) => setPartsUsed(prev => prev.filter(p => p.part_id !== partId));

  const handlePartScan = (code: string) => {
    // Match by part_id (e.g. "SP-001") or part_number
    const part = allParts.find(
      p => p.part_id?.toUpperCase() === code || p.part_number?.toUpperCase() === code,
    );
    if (!part) {
      // Show in select for manual confirmation
      return;
    }
    setPartsUsed(prev => {
      const existing = prev.find(p => p.part_id === part.id);
      if (existing) return prev.map(p => p.part_id === part.id ? { ...p, qty: p.qty + 1 } : p);
      return [...prev, { part_id: part.id, part_name: part.part_name, unit: part.unit, qty: 1 }];
    });
  };

  const signatureRequired = settings.require_signature_at_close;
  const signatureOk = !signatureRequired || !!signatureDataUrl;

  const isValid = signatureOk && (photoRequired ? hasPhoto : true) && (isPreventive
    ? (!pmProblemsEncountered || pmProblemDescription.trim().length > 0)
    : (
        (settings.require_failure_code_at_close ? failureCode.length > 0 : true) &&
        (settings.require_action_code_at_close ? actionCode.length > 0 : true) &&
        whatWasDone.trim().length > 0
      ));

  const handleCancel = () => {
    localStorage.removeItem(draftKey(workOrder.id));
    onOpenChange(false);
  };

  const handleSubmit = () => {
    if (!isValid) return;
    localStorage.removeItem(draftKey(workOrder.id));

    const selectedFC = activeFCs.find(c => c.code === failureCode);
    const selectedAC = activeACs.find(c => c.code === actionCode);

    if (isPreventive) {
      // Build checklist results for items that were checked
      const checklistResultEntries = Array.from(pmCheckedCodes).map(code => ({
        code,
        passed: pmChecklistResults[code]?.passed ?? null,
        notes: pmChecklistResults[code]?.notes || undefined,
      }));
      const data: ClosureData = {
        pm_problems_encountered: pmProblemsEncountered,
        pm_problem_description: pmProblemsEncountered ? pmProblemDescription : undefined,
        action_taken: pmWhatWasDone || undefined,
        action_code: actionCode || undefined,
        labor_minutes: durationMinutes > 0 ? durationMinutes : undefined,
        tested_validated: testedValidated,
        test_type: (testedValidated && testType) ? testType as TestType : undefined,
        checklist_results: checklistResultEntries.length > 0 ? checklistResultEntries : undefined,
        signature_data_url: signatureDataUrl ?? undefined,
        signature_captured_by: signatureDataUrl ? user?.name : undefined,
        signature_captured_at: signatureDataUrl ? new Date().toISOString() : undefined,
      };
      onConfirm(data);
    } else {
      const failureLabel = selectedFC ? selectedFC.label : failureNotes.trim();
      const data: ClosureData = {
        failure: failureLabel || failureNotes.trim(),
        failure_code: failureCode || undefined,
        action_taken: selectedAC ? `${selectedAC.label}${whatWasDone ? ' — ' + whatWasDone : ''}` : whatWasDone,
        action_code: actionCode || undefined,
        root_cause: rootCause.trim() || undefined,
        root_cause_code: rootCauseCode || undefined,
        fix_type: fixType,
        repeat_failure_detected: !!repeatFailureInfo,
        comment: comment || undefined,
        labor_minutes: durationMinutes > 0 ? durationMinutes : undefined,
        tested_validated: testedValidated,
        test_type: (testedValidated && testType) ? testType as TestType : undefined,
        parts_used_list: partsUsed.length > 0 ? partsUsed.map(p => ({ part_id: p.part_id, qty: p.qty })) : undefined,
        signature_data_url: signatureDataUrl ?? undefined,
        signature_captured_by: signatureDataUrl ? user?.name : undefined,
        signature_captured_at: signatureDataUrl ? new Date().toISOString() : undefined,
      };
      onConfirm(data);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel(); else onOpenChange(v); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-[hsl(var(--status-green))]" />
            {isPreventive ? t('maintenance:wo.close_pm_title') : t('maintenance:wo.close_title')}
          </DialogTitle>
          <DialogDescription>
            {workOrder.wo_id} — {workOrder.mold?.mold_id || workOrder.mold_id}
          </DialogDescription>
        </DialogHeader>

        {draftRestored && (
          <div className="flex items-center gap-2 rounded-md status-amber px-3 py-2 text-xs">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            {t('maintenance:wo.draft_restored')}
          </div>
        )}

        <div className="space-y-4">
          {isPreventive ? (
            <>
              {/* PM Activity Checklist — guided step-by-step */}
              {activeACs.length > 0 && (() => {
                const totalSteps = activeACs.length;
                const doneCount = activeACs.filter(ac => pmChecklistResults[ac.code]?.passed !== undefined && pmChecklistResults[ac.code]?.passed !== null).length;
                const pct = Math.round((doneCount / totalSteps) * 100);
                const currentAC = activeACs[Math.min(guidedStep, totalSteps - 1)];
                const currentResult = currentAC ? pmChecklistResults[currentAC.code] : undefined;
                const currentDone = currentResult?.passed !== undefined && currentResult?.passed !== null;
                const allDone = doneCount === totalSteps;

                return (
                  <div className="space-y-3">
                    {/* Progress bar */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="font-medium">{t('maintenance:wo.closure.pm_checklist')}</span>
                      <span>{doneCount}/{totalSteps} done</span>
                    </div>
                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                      <div
                        className={cn('h-full rounded-full transition-all duration-300', allDone ? 'bg-[hsl(var(--status-green))]' : 'bg-molten')}
                        style={{ width: `${pct}%` }}
                      />
                    </div>

                    {/* Step dots */}
                    <div className="flex items-center gap-1 flex-wrap">
                      {activeACs.map((ac, i) => {
                        const r = pmChecklistResults[ac.code];
                        const done = r?.passed !== undefined && r?.passed !== null;
                        const passed = r?.passed === true;
                        return (
                          <button
                            key={ac.code}
                            type="button"
                            onClick={() => setGuidedStep(i)}
                            className={cn(
                              'h-2.5 w-2.5 rounded-full transition-all border',
                              i === guidedStep ? 'scale-125 border-primary' :
                              done && passed ? 'bg-[hsl(var(--status-green))] border-[hsl(var(--status-green))]' :
                              done && !passed ? 'bg-[hsl(var(--status-red))] border-[hsl(var(--status-red))]' :
                              'bg-muted border-border',
                            )}
                            title={ac.label}
                          />
                        );
                      })}
                    </div>

                    {/* Current step card */}
                    {currentAC && (
                      <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-4">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-[11px] text-muted-foreground mb-1">Step {guidedStep + 1} of {totalSteps}</p>
                            <p className="font-semibold text-base leading-snug">{currentAC.label}</p>
                          </div>
                          {currentDone && (
                            <span className={cn(
                              'text-xs font-bold px-2 py-0.5 rounded-full shrink-0',
                              currentResult?.passed ? 'bg-[hsl(var(--status-green-bg))] text-[hsl(var(--status-green))]' : 'bg-[hsl(var(--status-red-bg))] text-[hsl(var(--status-red))]',
                            )}>
                              {currentResult?.passed ? '✓ Pass' : '✗ Fail'}
                            </span>
                          )}
                        </div>

                        {/* Pass / Fail buttons */}
                        <div className="flex gap-3">
                          <button
                            type="button"
                            onClick={() => {
                              setPmChecklistResults(prev => ({ ...prev, [currentAC.code]: { ...prev[currentAC.code], passed: true, notes: prev[currentAC.code]?.notes ?? '' } }));
                              togglePmCode(currentAC.code, currentAC.label);
                              if (guidedStep < totalSteps - 1) setGuidedStep(s => s + 1);
                            }}
                            className={cn(
                              'flex-1 py-3 rounded-lg border-2 font-semibold text-sm transition-colors min-h-[52px]',
                              currentResult?.passed === true
                                ? 'bg-[hsl(var(--status-green))] border-[hsl(var(--status-green))] text-white'
                                : 'border-[hsl(var(--status-green)/0.4)] text-[hsl(var(--status-green))] hover:bg-[hsl(var(--status-green-bg))]',
                            )}
                          >
                            ✓ Pass
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setPmChecklistResults(prev => ({ ...prev, [currentAC.code]: { ...prev[currentAC.code], passed: false, notes: prev[currentAC.code]?.notes ?? '' } }));
                              if (!pmCheckedCodes.has(currentAC.code)) togglePmCode(currentAC.code, currentAC.label);
                              if (guidedStep < totalSteps - 1) setGuidedStep(s => s + 1);
                            }}
                            className={cn(
                              'flex-1 py-3 rounded-lg border-2 font-semibold text-sm transition-colors min-h-[52px]',
                              currentResult?.passed === false
                                ? 'bg-[hsl(var(--status-red))] border-[hsl(var(--status-red))] text-white'
                                : 'border-[hsl(var(--status-red)/0.4)] text-[hsl(var(--status-red))] hover:bg-[hsl(var(--status-red-bg))]',
                            )}
                          >
                            ✗ Fail
                          </button>
                        </div>

                        {/* Prev / Next navigation */}
                        <div className="flex items-center justify-between pt-1">
                          <button
                            type="button"
                            onClick={() => setGuidedStep(s => Math.max(0, s - 1))}
                            disabled={guidedStep === 0}
                            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed px-1 py-1 min-h-[36px]"
                          >
                            ← Previous
                          </button>
                          <button
                            type="button"
                            onClick={() => setGuidedStep(s => Math.min(totalSteps - 1, s + 1))}
                            disabled={guidedStep === totalSteps - 1}
                            className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed px-1 py-1 min-h-[36px]"
                          >
                            Next →
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Completed summary */}
                    {allDone && (
                      <div className="rounded-md status-green px-3 py-2 text-xs flex items-center gap-2">
                        <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                        All {totalSteps} items reviewed — {activeACs.filter(ac => pmChecklistResults[ac.code]?.passed === false).length > 0
                          ? `${activeACs.filter(ac => pmChecklistResults[ac.code]?.passed === false).length} failed item(s) noted`
                          : 'all passed'}
                      </div>
                    )}
                  </div>
                );
              })()}

              <div className="space-y-2">
                <Label>{t('maintenance:wo.closure.additional_notes_optional')}</Label>
                <Textarea
                  value={pmWhatWasDone}
                  onChange={(e) => setPmWhatWasDone(e.target.value)}
                  placeholder={t('maintenance:wo.closure.additional_notes_placeholder')}
                  className="min-h-[60px]"
                />
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="pm_problems_close"
                  checked={pmProblemsEncountered}
                  onCheckedChange={(checked) => setPmProblemsEncountered(!!checked)}
                />
                <Label htmlFor="pm_problems_close" className="cursor-pointer">Problems encountered during PM?</Label>
              </div>

              {pmProblemsEncountered && (
                <div className="space-y-2">
                  <Label>{t('maintenance:wo.closure.problem_encountered')}</Label>
                  <Textarea
                    value={pmProblemDescription}
                    onChange={(e) => setPmProblemDescription(e.target.value)}
                    placeholder={t('maintenance:wo.closure.problem_encountered_placeholder')}
                    className="min-h-[60px]"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              {/* Failure Code */}
              <div className="space-y-2">
                <Label>{t('maintenance:wo.closure.failure_required')}</Label>
                {activeFCs.length > 0 ? (
                  <Select value={failureCode} onValueChange={setFailureCode}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('maintenance:wo.closure.failure_select_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeFCs.map(fc => (
                        <SelectItem key={fc.code} value={fc.code}>
                          <span className="font-mono text-xs text-muted-foreground mr-2">{fc.code}</span>
                          {fc.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    value={failureNotes}
                    onChange={(e) => setFailureNotes(e.target.value)}
                    placeholder={t('maintenance:wo.closure.failure_placeholder')}
                  />
                )}
                {activeFCs.length > 0 && (
                  <Textarea
                    value={failureNotes}
                    onChange={(e) => setFailureNotes(e.target.value)}
                    placeholder="Additional failure details (optional)"
                    className="min-h-[48px]"
                  />
                )}
                {repeatFailureInfo && (
                  <div className="flex items-start gap-2 rounded-md status-amber px-3 py-2 text-xs">
                    <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">
                        Repeat failure: this code appeared {repeatFailureInfo.count}x on this mold in the last {settings.chronic_window_days ?? 90} days
                      </p>
                      {repeatFailureInfo.lastWO && (
                        <p className="mt-0.5 opacity-80">
                          Last: {repeatFailureInfo.lastWO.wo_id} — {repeatFailureInfo.lastWO.fix_type === 'temporary' ? 'temp fix' : 'permanent fix'}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Action Code */}
              <div className="space-y-2">
                <Label>{t('maintenance:wo.closure.action_taken')}</Label>
                {activeACs.length > 0 && (
                  <Select value={actionCode} onValueChange={setActionCode}>
                    <SelectTrigger>
                      <SelectValue placeholder={t('maintenance:wo.closure.action_select_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeACs.map(ac => (
                        <SelectItem key={ac.code} value={ac.code}>
                          <span className="font-mono text-xs text-muted-foreground mr-2">{ac.code}</span>
                          {ac.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Textarea
                  value={whatWasDone}
                  onChange={(e) => setWhatWasDone(e.target.value)}
                  placeholder={activeACs.length > 0 ? "Additional details (optional if action selected above)" : "What was done to fix it?"}
                  className="min-h-[70px]"
                />
              </div>

              {/* Root cause */}
              <div className="space-y-2">
                <Label>{activeRCs.length > 0 ? t('maintenance:wo.closure.root_cause') : t('maintenance:wo.closure.root_cause_optional')}</Label>
                {activeRCs.length > 0 ? (
                  <>
                    <Select value={rootCauseCode} onValueChange={setRootCauseCode}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder={t('maintenance:wo.closure.root_cause_select_placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeRCs.map(rc => (
                          <SelectItem key={rc.code} value={rc.code}>
                            <span className="font-mono text-xs mr-2">{rc.code}</span>
                            {rc.label}
                            <span className="text-muted-foreground ml-1 text-xs">
                              ({rootCauseCategoryLabels[rc.category as RootCauseCategory] ?? rc.category})
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Textarea
                      value={rootCause}
                      onChange={(e) => setRootCause(e.target.value)}
                      placeholder="Additional root cause notes (optional)"
                      className="min-h-[40px]"
                    />
                  </>
                ) : (
                  <Textarea
                    value={rootCause}
                    onChange={(e) => setRootCause(e.target.value)}
                    placeholder="What caused this failure? (material, wear, operator error...)"
                    className="min-h-[50px]"
                  />
                )}
              </div>

              {/* Fix type (2B) */}
              <div className="space-y-2">
                <Label>{t('maintenance:wo.closure.fix_type')}</Label>
                <div className="flex gap-2">
                  {(['permanent', 'temporary'] as FixType[]).map(ft => (
                    <button
                      key={ft}
                      type="button"
                      onClick={() => setFixType(ft)}
                      className={cn(
                        'flex-1 py-2 rounded-md text-sm font-medium border transition-all capitalize',
                        fixType === ft
                          ? ft === 'temporary'
                            ? 'border-[hsl(var(--status-amber))] bg-[hsl(var(--status-amber-bg))] text-[hsl(var(--status-amber))]'
                            : 'border-[hsl(var(--status-green))] bg-[hsl(var(--status-green-bg))] text-[hsl(var(--status-green))]'
                          : 'border-border text-muted-foreground hover:bg-muted',
                      )}
                    >
                      {ft}
                    </button>
                  ))}
                </div>
                {fixType === 'temporary' && (
                  <p className="text-xs text-[hsl(var(--status-amber))]">Schedule a follow-up repair to apply a permanent fix.</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>{t('maintenance:wo.closure.comment_optional')}</Label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('maintenance:wo.closure.comment_placeholder')}
                  className="min-h-[50px]"
                />
              </div>
            </>
          )}

          {/* Parts Used — corrective only */}
          {showPartsPicker && (
            <div className="space-y-2 pt-2 border-t">
              <Label>{t('maintenance:wo.closure.parts_used')}</Label>

              {/* Quick-add common failure parts */}
              {commonFailureParts.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {commonFailureParts.map(mp => {
                    const p = allParts.find(x => x.id === mp.part_id);
                    if (!p) return null;
                    const alreadyAdded = partsUsed.some(x => x.part_id === p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          if (!alreadyAdded) {
                            setPartsUsed(prev => [...prev, { part_id: p.id, part_name: p.part_name, unit: p.unit, qty: 1 }]);
                          }
                        }}
                        disabled={alreadyAdded}
                        className={cn(
                          'text-xs px-2 py-1 rounded border transition-colors',
                          alreadyAdded
                            ? 'border-border text-muted-foreground bg-muted cursor-default'
                            : 'border-primary/40 text-primary hover:bg-primary/5'
                        )}
                      >
                        + {p.part_name}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Inline picker */}
              <div className="flex gap-2">
                <Select value={selectedPartId} onValueChange={setSelectedPartId}>
                  <SelectTrigger className="flex-1 h-8 text-sm">
                    <SelectValue placeholder="Select part…" />
                  </SelectTrigger>
                  <SelectContent>
                    {allParts.filter(p => p.status === 'Active').map(p => (
                      <SelectItem key={p.id} value={p.id} className="text-sm">
                        {p.part_id} — {p.part_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  min={1}
                  value={partQty}
                  onChange={e => setPartQty(e.target.value)}
                  className="w-16 h-8 text-sm"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 px-2"
                  onClick={addPartUsed}
                  disabled={!selectedPartId}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 px-2"
                  onClick={() => setPartScannerOpen(true)}
                  title={t('maintenance:wo.closure.scan_part_barcode')}
                >
                  <ScanLine className="h-3.5 w-3.5" />
                </Button>
              </div>

              <BarcodeScanner
                open={partScannerOpen}
                onOpenChange={setPartScannerOpen}
                onScan={handlePartScan}
                title={t('maintenance:wo.closure.scan_part_barcode')}
                description={t('maintenance:wo.closure.scan_part_description')}
              />

              {/* Selected parts list with cost */}
              {partsUsed.length > 0 && (
                <div className="space-y-1 mt-1">
                  {partsUsed.map(p => {
                    const partData = allParts.find(x => x.id === p.part_id);
                    const lineCost = partData?.unit_cost != null ? partData.unit_cost * p.qty : null;
                    const stockAfter = partData ? (partData.stock_on_hand ?? 0) - p.qty : null;
                    const negative = stockAfter != null && stockAfter < 0;
                    return (
                      <div key={p.part_id} className="flex items-center justify-between text-sm py-1 px-2 bg-muted rounded">
                        <span>
                          {p.part_name} <span className="text-muted-foreground">× {p.qty} {p.unit}</span>
                          {stockAfter != null && (
                            <span className={cn(
                              'ml-2 text-[11px] font-mono',
                              negative ? 'text-destructive' : 'text-muted-foreground',
                            )}>
                              ({t('maintenance:wo.closure.stock_after', { count: stockAfter })}{negative ? t('maintenance:wo.closure.stock_insufficient') : ''})
                            </span>
                          )}
                        </span>
                        <div className="flex items-center gap-2">
                          {lineCost != null && (
                            <span className="text-xs text-muted-foreground font-mono">${lineCost.toFixed(2)}</span>
                          )}
                          <button type="button" onClick={() => removePartUsed(p.part_id)} className="text-muted-foreground hover:text-destructive">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {(() => {
                    const total = partsUsed.reduce((sum, p) => {
                      const cost = allParts.find(x => x.id === p.part_id)?.unit_cost;
                      return cost != null ? sum + cost * p.qty : sum;
                    }, 0);
                    return total > 0 ? (
                      <div className="flex items-center justify-between text-xs text-muted-foreground px-2 pt-1 border-t">
                        <span>{t('maintenance:wo.closure.estimated_parts_cost')}</span>
                        <span className="font-mono font-semibold text-foreground">${total.toFixed(2)}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
          )}

          {/* Tested / Validated (2A) — shared for both preventive and corrective */}
          <div className="space-y-2 pt-2 border-t">
            <div className="flex items-center gap-2">
              <Checkbox
                id="tested_validated"
                checked={testedValidated}
                onCheckedChange={(v) => { setTestedValidated(!!v); if (!v) setTestType(''); }}
              />
              <Label htmlFor="tested_validated" className="cursor-pointer font-normal">
                Repair was tested before returning to production
              </Label>
            </div>
            {testedValidated && (
              <Select value={testType} onValueChange={(v) => setTestType(v as TestType)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder={t('maintenance:wo.closure.test_select_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inspection">{t('maintenance:wo.closure.test_visual_inspection')}</SelectItem>
                  <SelectItem value="dry_cycle">{t('maintenance:wo.closure.test_dry_cycle')}</SelectItem>
                  <SelectItem value="production_test">{t('maintenance:wo.closure.test_production_test')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Time Log Summary + Additional Time */}
          <div className="space-y-3">
            <Label className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              Time
            </Label>

            {/* Logged time entries */}
            {timeEntries.length > 0 && (
              <div className="rounded-md border bg-muted/30 p-3 space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground mb-1">{t('maintenance:wo.closure.logged_time_entries')}</p>
                {timeEntries.map(entry => (
                  <div key={entry.id} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {entry.technician_name} — {entry.activity}
                    </span>
                    <span className="font-mono font-medium">
                      {entry.minutes >= 60
                        ? `${Math.floor(entry.minutes / 60)}h ${entry.minutes % 60}m`
                        : `${entry.minutes}m`}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between text-xs font-medium pt-1.5 border-t">
                  <span>{t('maintenance:wo.closure.total_logged')}</span>
                  <span className="font-mono">
                    {totalLoggedMinutes >= 60
                      ? `${Math.floor(totalLoggedMinutes / 60)}h ${totalLoggedMinutes % 60}m`
                      : `${totalLoggedMinutes}m`}
                  </span>
                </div>
              </div>
            )}

            {/* Additional time input */}
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="0"
                step="5"
                value={durationMinutes || ''}
                onChange={(e) => setDurationMinutes(parseInt(e.target.value, 10) || 0)}
                placeholder="0"
                className="w-28"
              />
              <span className="text-sm text-muted-foreground">
                {timeEntries.length > 0 ? 'additional minutes' : 'minutes'}
              </span>
            </div>
            {timeEntries.length > 0 && durationMinutes > 0 && (
              <p className="text-xs text-muted-foreground">
                Grand total: {totalLoggedMinutes + durationMinutes} minutes
              </p>
            )}
          </div>
        </div>

        {/* Signature capture */}
        <div className="mt-4">
          <SignaturePad
            label={t('maintenance:wo.closure.signature')}
            value={signatureDataUrl}
            onChange={setSignatureDataUrl}
            width={Math.min(380, window.innerWidth - 80)}
            height={120}
          />
        </div>

        {photoRequired && !hasPhoto && (
          <div className="flex items-start gap-2 rounded-md status-amber px-3 py-2.5 mt-4">
            <Camera className="h-4 w-4 shrink-0 mt-0.5" />
            <p className="text-sm">
              <Trans i18nKey="maintenance:wo.closure.photo_required" components={{ strong: <strong /> }} />
            </p>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleCancel}>{t('common:action.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={!isValid} className="bg-[hsl(var(--status-green))] hover:bg-[hsl(var(--status-green))]/90 text-white">
            <CheckCircle className="h-4 w-4 mr-2" />
            {t('maintenance:wo.close_title')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
